import type { FlowContactTimeline } from '@shared/flow/contact-timeline';
import { nonBlank } from '@shared/flow/non-blank';
import { isLifecycleEmailSubject } from '@shared/flow/timeline-filter';
import type { EntitySignalRow } from '@shared/flow/entity-signals';
import { actionFields, fields, type DetailField } from '../actions/action-fields';
import { fmtDateOnlyText, dateOnlyDay, BARE_DATE } from '../account/accountFieldFormat';

/**
 * Pure contact-timeline normalisation (U1b, plan 005 Wave 0 / plan 004 §2 U1b).
 * Lifted out of `ContactTimeline.tsx` so U5's Glaze `ContactTimelineCard` and
 * V1's `ContactTimeline` consume the SAME entry stream, kind vocabulary, and
 * filtering — never a second re-derivation. React-free, Glaze-free: this
 * module only shapes data, never fetches it (callers pass in the already-
 * fetched `FlowContactTimeline` + signal rows from `useFlowContactTimeline`/
 * `useFlowContactSignals`).
 *
 * Every function here is a byte-for-byte port of `ContactTimeline.tsx`'s
 * former private `normalize`/`fmtDate`/`join`/`fmtArr`/`workspaceDetailFields`
 * — zero behaviour change is the acceptance bar (V1's own suite passes
 * unmodified against the imported versions).
 */

export type EntryKind = 'signal' | 'touchpoint' | 'task' | 'event' | 'campaign' | 'workspace' | 'action';

export interface Entry {
  id: string;
  kind: EntryKind;
  date: string | null;
  title: string;
  /** One-line subtitle shown on the collapsed row. */
  subtitle: string | null;
  /** Longer free-text (description) revealed on expand. */
  description: string | null;
  /** Label/value rows revealed on expand. */
  fields: DetailField[];
  /**
   * Workspace deep-link (`https://app.clay.com/workspaces/{id}`), populated
   * only for `kind: 'workspace'` entries with a real `workspaceId`
   * (terra-middleware PR #254 — `Workspace__r.Workspace_ID__c`).
   */
  workspaceUrl?: string | null;
  /**
   * Main vs. Separate, for `kind: 'workspace'` entries only — compares the
   * workspace's `workspaceAccountId` (SF account id) against the VIEWED
   * CONTACT's own `accountId`. `null` whenever either side is unavailable —
   * a missing comparator must never render a wrong badge.
   */
  workspaceBadge?: 'main' | 'separate' | null;
  /** Set only for `kind: 'action'` entries — the raw row `ActionRow` renders from. */
  signalRow?: EntitySignalRow;
}

export const KIND_LABEL: Record<EntryKind, string> = {
  signal: 'Signals',
  touchpoint: 'Touchpoints',
  task: 'Tasks',
  event: 'Events',
  campaign: 'Campaigns',
  workspace: 'Workspace',
  action: 'Actions',
};

/** Filter-chip order — insertion order of `KIND_LABEL`, never re-sorted. */
export const KIND_ORDER = Object.keys(KIND_LABEL) as EntryKind[];

/** Both renderers cap the rendered list at this many rows. */
export const TIMELINE_RENDER_CAP = 50;

/** `subscriptionArr` formatted like the prototype's contact-detail treatment
 *  ($0 for zero, `$XXXK` otherwise) — `null`/`undefined` renders nothing. */
export function fmtArr(arr: number | null | undefined): string | null {
  if (arr === null || arr === undefined) return null;
  return arr > 0 ? `$${Math.round(arr / 1000)}K` : '$0';
}

/** Drop empty parts, join the rest — for one-line subtitles. */
export function join(...parts: (string | null | undefined)[]): string | null {
  const kept = parts.filter((p): p is string => !!p && p.trim() !== '');
  return kept.length ? kept.join(' · ') : null;
}

/** Label/value rows for a workspace entry's expanded detail — mirrors the
 *  prototype's `contactDetail()` treatment (Workspace Tier / Active Trial /
 *  Subscription ARR). Built manually rather than via the generic `fields()`
 *  helper, since `false`/`0` are valid values here (not "empty") — `fields()`'s
 *  falsy-string filter would wrongly drop them. */
export function workspaceDetailFields(w: FlowContactTimeline['workspaceUsers'][number]): DetailField[] {
  const out: DetailField[] = [];
  if (w.relationshipType) out.push({ label: 'Relationship', value: w.relationshipType });
  if (w.productTier) out.push({ label: 'Product Tier', value: w.productTier });
  if (w.activeTrial !== null && w.activeTrial !== undefined) {
    out.push({ label: 'Active Trial', value: w.activeTrial ? 'Yes' : 'No' });
  }
  const arr = fmtArr(w.subscriptionArr);
  if (arr !== null) out.push({ label: 'Subscription ARR', value: arr });
  return out;
}

export function normalizeTimelineEntries(
  data: FlowContactTimeline,
  contactAccountId: string | null,
  signalRows: EntitySignalRow[],
): Entry[] {
  const entries: Entry[] = [
    ...signalRows.map(row => ({
      id: `act-${row.signal_id}`, kind: 'action' as const, date: row.event_date ?? dateOnlyDay(row.due_date),
      title: row.signal_name,
      subtitle: row.action_state,
      description: null,
      fields: actionFields(row),
      signalRow: row,
    })),
    ...data.signals.map(s => ({
      id: `sig-${s.signalId}`, kind: 'signal' as const, date: s.eventDate ?? s.createdAt,
      title: join(s.type, s.subtype) ?? 'Signal',
      subtitle: s.description,
      description: s.description,
      fields: fields(['Type', s.type], ['Subtype', s.subtype]),
    })),
    ...data.touchpoints.map(t => ({
      id: `tp-${t.touchpointId}`, kind: 'touchpoint' as const, date: t.touchpointDate ?? t.createdAt,
      // U7 — rep wording for Salesforce's raw Touchpoint Type/Detail values. NOT applied to the
      // Signal rows above: a Signal Type "Touchpoint" is Terra's own vocabulary.
      title: nonBlank(t.type) ?? 'Touchpoint',
      subtitle: nonBlank(t.touchpointDetail) ?? nonBlank(t.description),
      description: t.description,
      fields: fields(
        ['Type', t.type],
        ['Detail', t.touchpointDetail],
        ['AI Referral Source', t.aiReferralSourceAnswer],
      ),
    })),
    ...data.tasks.map(t => ({
      id: `task-${t.taskId}`, kind: 'task' as const, date: t.activityDate ?? t.createdAt,
      title: t.subject ?? 'Task',
      subtitle: join(t.status, t.ownerName),
      description: t.description,
      fields: fields(
        ['Status', t.status],
        ['Priority', t.priority],
        ['Type', join(t.type, t.subtype)],
        ['Owner', t.ownerName],
      ),
    })),
    ...data.events.map(e => ({
      id: `ev-${e.eventId}`, kind: 'event' as const, date: e.startDateTime ?? e.createdAt,
      title: e.subject ?? 'Event',
      subtitle: join(e.type, e.ownerName),
      description: e.description,
      fields: fields(
        ['Type', join(e.type, e.subtype)],
        ['Location', e.location],
        ['Owner', e.ownerName],
        ['Attendees', e.attendees.length
          ? e.attendees.map(a => a.name).filter(Boolean).join(', ') || `${e.attendees.length}`
          : null],
      ),
    })),
    ...data.campaignMemberships.map(c => ({
      id: `cm-${c.campaignMemberId}`, kind: 'campaign' as const, date: c.createdDate,
      title: c.campaignName ?? 'Campaign',
      subtitle: join(c.campaignType, c.status),
      description: c.description,
      fields: fields(['Type', c.campaignType], ['Status', c.status], ['Notes', c.campaignMemberNotes ?? null]),
    })),
    ...data.workspaceUsers.map(w => {
      const workspaceId = w.workspaceId ?? null;
      const workspaceAccountId = w.workspaceAccountId ?? null;
      const workspaceBadge: Entry['workspaceBadge'] =
        workspaceAccountId !== null && contactAccountId !== null
          ? (workspaceAccountId === contactAccountId ? 'main' : 'separate')
          : null;
      return {
        id: `ws-${w.workspaceUserId}`, kind: 'workspace' as const, date: w.workspaceCreatedAt,
        title: w.workspaceName ?? 'Workspace',
        subtitle: w.relationshipType,
        description: null,
        fields: workspaceDetailFields(w),
        workspaceUrl: workspaceId ? `https://app.clay.com/workspaces/${workspaceId}` : null,
        workspaceBadge,
      };
    }),
  ];
  // Exclude undated entries (no clear place on the timeline) and lifecycle
  // "Email Delivered:" tasks (auto-sends that flood the feed) — the
  // lifecycle-email check only applies to Middleman task/email titles, never
  // to `action` entries (terra_signals rows have no such concept).
  return entries
    .filter(e => !!e.date && (e.kind === 'action' || !isLifecycleEmailSubject(e.title)))
    .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));
}

export function fmtTimelineDate(iso: string | null): string {
  if (!iso) return '—';
  // A bare YYYY-MM-DD is a DATE (the due_date fallback), not an instant.
  if (BARE_DATE.test(iso)) return fmtDateOnlyText(iso, { month: 'short', day: 'numeric' }) ?? '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Per-kind entry counts, keyed by `EntryKind` — shared so the filter chips
 *  and any card summary can't drift into different counts. */
export function countByKind(entries: Entry[]): Record<EntryKind, number> {
  const c = {} as Record<EntryKind, number>;
  for (const e of entries) c[e.kind] = (c[e.kind] ?? 0) + 1;
  return c;
}

/** Entries matching `mode`, or all entries when `mode` is `null` — the same
 *  filter both the chip row and the card key off. Not itself capped at
 *  `TIMELINE_RENDER_CAP`; render-time slicing is a separate concern. */
export function visibleEntries(entries: Entry[], mode: EntryKind | null): Entry[] {
  return mode ? entries.filter(e => e.kind === mode) : entries;
}
